import {
  hasPermission,
  Permission,
} from '@core/permission/permission.constants';
import type {
  PortalAccessContext,
  PortalAudience,
} from '@/access/portalAccess.types';
import type { TimesheetsViewInput } from '@/modules/timesheets/timesheets.viewInput';

export type TimesheetTabKey = 'pending' | 'requiresApproval' | 'approved';

type TimesheetTabDefinition = {
  key: TimesheetTabKey;
  label: string;
  audiences: PortalAudience[];
};

/**
 * Tabs in display order.
 * Awaiting submission is scheduled shifts the candidate has not submitted. Only tenant
 * staff who can act for a candidate see it. Client users and client admins
 * stay on Requires Approval and Approved.
 */
const TIMESHEET_TABS: TimesheetTabDefinition[] = [
  {
    key: 'pending',
    label: 'Awaiting submission',
    audiences: ['tenant'],
  },
  {
    key: 'requiresApproval',
    label: 'Requires approval',
    audiences: ['client', 'tenant'],
  },
  {
    key: 'approved',
    label: 'Approved',
    audiences: ['client', 'tenant'],
  },
];

export type TimesheetsView = {
  audience: PortalAudience;
  tabs: TimesheetTabKey[];
  defaultTab: TimesheetTabKey;
  actions: {
    showClientName: boolean;
    showClientFilter: boolean;
    /** Record a missing shift for the candidate via createDaySequences. */
    submitOnBehalf: boolean;
    approveTimesheets: boolean;
  };
  query: {
    missingTimesheets: { fetch: boolean };
  };
};

/**
 * Tenant user, tenant admin, and HQ admin. Recruiters and the client portal
 * cannot see or submit pending timesheets.
 */
const canManagePendingTimesheets = (access: PortalAccessContext): boolean =>
  access.isTenantUser || access.isTenantAdmin || access.isHqAdmin;

const visibleTabs = (access: PortalAccessContext): TimesheetTabKey[] =>
  TIMESHEET_TABS.filter((tab) => {
    if (!tab.audiences.includes(access.audience)) {
      return false;
    }
    if (tab.key === 'pending' && !canManagePendingTimesheets(access)) {
      return false;
    }
    return true;
  }).map((tab) => tab.key);

export const getTimesheetTabs = (tabKeys: TimesheetTabKey[]) =>
  TIMESHEET_TABS.filter((tab) => tabKeys.includes(tab.key)).map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

export const getTimesheetsView = ({
  access,
}: TimesheetsViewInput): TimesheetsView => {
  const tabs = visibleTabs(access);
  const canManagePending = canManagePendingTimesheets(access);

  return {
    audience: access.audience,
    tabs,
    defaultTab: tabs.includes('requiresApproval')
      ? 'requiresApproval'
      : (tabs[0] ?? 'requiresApproval'),
    actions: {
      showClientName: !access.isClientPortal,
      showClientFilter: access.isTenantPortal,
      submitOnBehalf: canManagePending,
      approveTimesheets: hasPermission(
        access.permissions,
        Permission.ApproveDaySequences,
      ),
    },
    query: {
      missingTimesheets: { fetch: canManagePending },
    },
  };
};
